import * as React from 'react'
import clsx from 'clsx'
import {
  FormControlContext,
  FormControlState,
  useFormControl,
  FormControlProps,
} from '../FormControl'
import { Typography } from '../Typography'
import { sx as sxFunc, SxProps } from '../sx'
import style from './index.module.scss'
import { capitalize } from '../../utils'

export interface InputAdornmentClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `variant="filled"`. */
  filled: string
  /** Styles applied to the root element if `variant="outlined"`. */
  outlined: string
  /** Styles applied to the root element if `variant="outlinedDash"`. */
  outlinedDash: string
  /** Styles applied to the root element if `variant="standard"`. */
  standard: string
  /** Styles applied to the root element if `position="start"`. */
  positionStart: string
  /** Styles applied to the root element if `position="end"`. */
  positionEnd: string
  /** Styles applied to the root element if `disablePointerEvents={true}`. */
  disablePointerEvents: string
  /** Styles applied to the input element if `size="small"`. */
  sizeSmall: string
  /** Styles applied to the input element if `size="medium"`. */
  sizeMedium: string
  /** Styles applied to the input element if `size="large"`. */
  sizeLarge: string
}

export interface InputAdornmentProps
  extends Partial<React.HTMLAttributes<HTMLDivElement>> {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<InputAdornmentClasses>
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * The content of the component, normally an `IconButton` or string.
   */
  children?: React.ReactNode
  /**
   * Disable pointer events on the root.
   * This allows for the content of the adornment to focus the `input` on click.
   * @default false
   */
  disablePointerEvents?: boolean
  /**
   * If children is a string then disable wrapping in a Typography component.
   * @default false
   */
  disableTypography?: boolean
  /**
   * The position this adornment should appear relative to the `Input`.
   */
  position: 'start' | 'end'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The variant to use.
   * Note: If you are using the `TextField` component or the `FormControl` component
   * you do not have to set this manually.
   */
  variant?: FormControlProps['variant']
}

export const InputAdornment = React.forwardRef<
  HTMLDivElement,
  InputAdornmentProps
>(function InputAdornment(props, ref) {
  const {
    children,
    classes,
    className,
    Component = 'div',
    disablePointerEvents = false,
    disableTypography = false,
    position,
    variant: variantProp,
    sx,
    ...other
  } = props

  const factorFormControl = useFormControl() || ({} as FormControlState)

  let variant = variantProp

  if (variantProp && factorFormControl.variant) {
    if (process.env.NODE_ENV !== 'production') {
      if (variantProp === factorFormControl.variant) {
        console.error(
          'The `InputAdornment` variant infers the variant prop ' +
            'you do not have to provide one.'
        )
      }
    }
  }

  if (factorFormControl && !variant) {
    variant = factorFormControl.variant
  }

  const _classes = clsx(
    [style.root, classes?.root],
    [
      style[`size-${factorFormControl.size}`],
      // @ts-ignore
      classes?.[`size${capitalize(factorFormControl.size)}`],
    ],
    disablePointerEvents && [
      style.disablePointerEvents,
      classes?.disablePointerEvents,
    ],
    // @ts-ignore
    position && [style[position], classes?.[`position${capitalize(position)}`]],
    variant && classes?.[variant],
    className,
    sxFunc(sx)
  )

  return (
    <FormControlContext.Provider value={undefined}>
      <Component className={_classes} ref={ref} {...other}>
        {typeof children === 'string' && !disableTypography ? (
          <Typography sx={{ textColor: 'neutral-600' }}>{children}</Typography>
        ) : (
          <React.Fragment>
            {/* To have the correct vertical alignment baseline */}
            {position === 'start' ? (
              /* notranslate needed while Google Translate will not fix zero-width space issue  refer: https://developers.google.com/search/blog/2008/10/helping-you-break-language-barrier*/
              <span className="notranslate">&#8203;</span>
            ) : null}
            {children}
          </React.Fragment>
        )}
      </Component>
    </FormControlContext.Provider>
  )
})
